import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { saveAs } from 'file-saver'
import apiGateway from '../../services/apiGateway'

export const SennaBatchReportContext = createContext(null)

const POLLING_INTERVAL_MS = 15000
const BASE_URL = '/farol-devolutivas-r/senna-batch-reports'

const getLatest = () => {
  return apiGateway.get(`${BASE_URL}/latest`)
}

const retryGeneration = generationId => {
  return apiGateway.post(`${BASE_URL}/${generationId}/retry`)
}

const downloadGeneration = generationId => {
  return apiGateway.get(`${BASE_URL}/${generationId}/download`, {
    responseType: 'blob',
  })
}

const markGenerationDownloaded = generationId => {
  return apiGateway.post(`${BASE_URL}/${generationId}/downloaded`)
}

const extractFileName = (contentDisposition, generationId) => {
  const fallback = `devolutivas-${generationId}.zip`

  if (!contentDisposition) {
    return fallback
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)

  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch (error) {
      return utf8Match[1]
    }
  }

  const normalMatch = contentDisposition.match(/filename="?([^";]+)"?/i)

  return normalMatch?.[1] || fallback
}

export const SennaBatchReportProvider = ({ children, enabled }) => {
  const [latestGeneration, setLatestGeneration] = useState(null)
  const [isLoadingLatest, setIsLoadingLatest] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const latestRequestRunning = useRef(false)

  const refreshLatest = useCallback(
    async ({ silent = false } = {}) => {
      if (!enabled || latestRequestRunning.current) {
        return null
      }

      latestRequestRunning.current = true

      if (!silent) {
        setIsLoadingLatest(true)
      }

      try {
        const response = await getLatest()

        if (response.status === 204) {
          setLatestGeneration(null)
          return null
        }

        setLatestGeneration(response.data)

        return response.data
      } catch (error) {
        return null
      } finally {
        latestRequestRunning.current = false

        if (!silent) {
          setIsLoadingLatest(false)
        }
      }
    },
    [enabled],
  )

  const registerCreatedGeneration = useCallback(generation => {
    setLatestGeneration(generation)
  }, [])

  const retryLatest = useCallback(async () => {
    if (!latestGeneration || latestGeneration.status !== 'ERROR') {
      return null
    }

    setIsRetrying(true)

    try {
      const response = await retryGeneration(latestGeneration.generationId)

      registerCreatedGeneration(response.data)

      return response.data
    } finally {
      setIsRetrying(false)
    }
  }, [latestGeneration, registerCreatedGeneration])

  const downloadLatest = useCallback(async () => {
    if (!latestGeneration || latestGeneration.status !== 'FINISHED') {
      return false
    }

    const { generationId } = latestGeneration

    setIsDownloading(true)

    try {
      const response = await downloadGeneration(generationId)

      const contentDisposition = response.headers['content-disposition']

      const fileName = extractFileName(contentDisposition, generationId)

      await markGenerationDownloaded(generationId)

      saveAs(response.data, fileName)
      setLatestGeneration(null)

      return true
    } finally {
      setIsDownloading(false)
    }
  }, [latestGeneration])

  useEffect(() => {
    if (!enabled) {
      setLatestGeneration(null)
      return
    }

    refreshLatest()
  }, [enabled, refreshLatest])

  useEffect(() => {
    if (!enabled || latestGeneration?.status !== 'PROCESSING') {
      return undefined
    }

    const intervalId = window.setInterval(() => {
      refreshLatest({ silent: true })
    }, POLLING_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, latestGeneration?.status, refreshLatest])

  const contextValue = useMemo(
    () => ({
      latestGeneration,
      isLoadingLatest,
      isRetrying,
      isDownloading,
      refreshLatest,
      registerCreatedGeneration,
      retryLatest,
      downloadLatest,
    }),
    [
      latestGeneration,
      isLoadingLatest,
      isRetrying,
      isDownloading,
      refreshLatest,
      registerCreatedGeneration,
      retryLatest,
      downloadLatest,
    ],
  )

  return (
    <SennaBatchReportContext.Provider value={contextValue}>
      {children}
    </SennaBatchReportContext.Provider>
  )
}

SennaBatchReportProvider.propTypes = {
  children: PropTypes.node.isRequired,
  enabled: PropTypes.bool,
}

SennaBatchReportProvider.defaultProps = {
  enabled: false,
}

export const useSennaBatchReport = () => {
  const context = useContext(SennaBatchReportContext)

  if (!context) {
    throw new Error('useSennaBatchReport deve ser usado dentro de SennaBatchReportProvider')
  }

  return context
}
